import { useMemo, useState, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useActiveEntities } from '@/stores/selectors';
import { ENTITY_META, entityColor } from '@/config/entityMeta';
import type { EntityType } from '@/types/entity';
import { cn } from '@/lib/cn';

export function EntityPicker({
  value,
  onSelect,
  types,
  exclude,
  placeholder = '엔티티 검색…',
  trigger,
}: {
  value?: string;
  onSelect: (id: string) => void;
  types?: EntityType[];
  exclude?: string[];
  placeholder?: string;
  trigger: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const all = useActiveEntities();

  const results = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all
      .filter(
        (e) =>
          (!types || types.includes(e.type)) &&
          !exclude?.includes(e.id) &&
          (!ql ||
            e.name.toLowerCase().includes(ql) ||
            e.aliases?.some((a) => a.toLowerCase().includes(ql)) ||
            e.summary?.toLowerCase().includes(ql)),
      )
      .slice(0, 50);
  }, [all, q, types, exclude]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <div className="border-b border-line p-2">
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="h-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {results.length === 0 && <p className="px-3 py-4 text-xs text-faint">결과 없음</p>}
          {results.map((e) => {
            const M = ENTITY_META[e.type];
            const Icon = M.icon;
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => {
                  onSelect(e.id);
                  setOpen(false);
                  setQ('');
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition hover:bg-panel-h',
                  value === e.id && 'bg-panel-h',
                )}
              >
                <Icon className="size-3.5 flex-none" style={{ color: entityColor(e) }} />
                <span className="truncate text-ink">{e.name}</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wide text-faint">
                  {M.label}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
